import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuthContext } from "../contexts/AuthContext";
import ScheduleModal from "../components/ScheduleModal";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminInstagram.module.css";
import { isQBankCode, QBankCode } from "../utils/qbankProfile";

interface InstagramContent {
    id: string;
    question_id: string;
    status: "draft" | "generating" | "ready" | "posted";
    scheduled_for: string | null;
    scheduling_status: "unscheduled" | "scheduled" | "posting" | "completed" | "failed";
    scheduling_error: string | null;
    images: {
        slide1_question?: string;
        slide2_answer?: string;
        slide3_sources?: string;
    } | null;
    caption: string | null;
    hashtags: string[] | null;
    posted_at: string | null;
    instagram_post_url: string | null;
    instagram_media_id: string | null;
    publish_attempts: number | null;
    last_publish_error: string | null;
    generation_error: string | null;
    created_at: string;
    updated_at: string;
    qbank_questions?: {
        id?: string;
        question_text: string;
        specialty: string;
        correct_answer?: string;
        options?: { label: string; text: string }[];
    };
}

interface EligibleCollection {
    id: string;
    title: string;
    year: number;
    month: number;
    questions_count: number;
    eligible_count: number;
}

interface EligibleQuestion {
    id: string;
    question_text: string;
    specialty: string;
    correct_answer: string;
    options: { label: string; text: string }[];
    difficulty?: string;
    question_references?: {
        text_excerpt: string;
        source_name: string;
    }[];
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    draft: { label: "Draft", color: "#937663" },
    generating: { label: "Generating...", color: "#faad33" },
    ready: { label: "Ready", color: "#39a3a1" },
    posted: { label: "Posted", color: "#1fc16b" },
};

const SCHEDULING_STATUS_LABELS: Record<string, { label: string; color: string }> = {
    unscheduled: { label: "Not Scheduled", color: "#937663" },
    scheduled: { label: "Scheduled", color: "#2563eb" },
    posting: { label: "Posting...", color: "#faad33" },
    completed: { label: "Posted", color: "#1fc16b" },
    failed: { label: "Failed", color: "#dc2626" },
};

const AdminInstagram: React.FC = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [contents, setContents] = useState<InstagramContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<"all" | "ready" | "scheduled" | "posted">("all");
    const initialBank = searchParams.get("bank") || undefined;
    const [activeBank, setActiveBank] = useState<QBankCode>(isQBankCode(initialBank) ? initialBank : "sdle");

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Modals
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState<InstagramContent | null>(null);
    const [schedulingContentId, setSchedulingContentId] = useState<string | null>(null);
    const [schedulingInitialDate, setSchedulingInitialDate] = useState<string | null>(null);

    // Picker state
    const [pickerCollections, setPickerCollections] = useState<EligibleCollection[]>([]);
    const [pickerQuestions, setPickerQuestions] = useState<EligibleQuestion[]>([]);
    const [pickerTotalEligible, setPickerTotalEligible] = useState(0);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>("");
    const [generating, setGenerating] = useState(false);

    // Picker navigation
    const [pickerView, setPickerView] = useState<"collections" | "questions">("collections");
    const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

    // Reference selection
    const [selectedRefIndices, setSelectedRefIndices] = useState<Set<number>>(new Set());

    // Search and filter
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerSpecialtyFilter, setPickerSpecialtyFilter] = useState("all");

    // Edit mode
    const [editingCaption, setEditingCaption] = useState(false);
    const [editedCaption, setEditedCaption] = useState("");
    const [editedHashtags, setEditedHashtags] = useState<string[]>([]);

    // Publishing state
    const [publishing, setPublishing] = useState(false);
    const [publishError, setPublishError] = useState<string | null>(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    const loadContents = useCallback(async () => {
        if (!user?.id) return;

        try {
            // Build query params based on active tab
            const params = new URLSearchParams({
                user_id: user.id,
                page: page.toString(),
                bank: activeBank,
            });

            if (activeTab === "scheduled") {
                params.set("scheduling_status", "scheduled");
            } else if (activeTab !== "all") {
                params.set("status", activeTab);
            }

            const res = await authenticatedFetch(`${apiUrl}/api/admin/instagram?${params}`);
            if (res.ok) {
                const data = await res.json();
                setContents(data.items || []);
                setTotalPages(data.pages || 1);
            }
        } catch (err) {
            console.error("Failed to load Instagram content:", err);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, user?.id, page, activeTab, activeBank]);

    useEffect(() => {
        loadContents();
    }, [loadContents]);

    // Polling for generating or posting status
    useEffect(() => {
        const hasGenerating = contents.some((c) => c.status === "generating");
        const hasPosting = contents.some((c) => c.scheduling_status === "posting");
        if (hasGenerating || hasPosting) {
            const interval = setInterval(loadContents, 3000);
            return () => clearInterval(interval);
        }
    }, [contents, loadContents]);

    const loadCollections = async () => {
        if (!user?.id) return;
        setLoadingCollections(true);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/eligible-questions?user_id=${user.id}&bank=${activeBank}`
            );
            if (res.ok) {
                const data = await res.json();
                setPickerCollections(data.collections || []);
                setPickerTotalEligible(data.total_eligible || 0);
            }
        } catch (err) {
            console.error("Failed to load collections:", err);
        } finally {
            setLoadingCollections(false);
        }
    };

    const loadCollectionQuestions = async (collectionId: string) => {
        if (!user?.id) return;
        setLoadingQuestions(true);
        setPickerQuestions([]);
        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/eligible-questions?user_id=${user.id}&collection_id=${collectionId}&bank=${activeBank}`
            );
            if (res.ok) {
                const data = await res.json();
                setPickerQuestions(data.questions || []);
            }
        } catch (err) {
            console.error("Failed to load questions:", err);
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleSelectCollection = (colId: string) => {
        setSelectedCollectionId(colId);
        setPickerView("questions");
        setSelectedQuestionId("");
        setSelectedRefIndices(new Set());
        setPickerSearch("");
        setPickerSpecialtyFilter("all");
        loadCollectionQuestions(colId);
    };

    const handlePickerBack = () => {
        setPickerView("collections");
        setSelectedCollectionId(null);
        setSelectedQuestionId("");
        setSelectedRefIndices(new Set());
        setPickerSearch("");
        setPickerSpecialtyFilter("all");
    };

    const handleOpenGenerateModal = () => {
        setShowGenerateModal(true);
        setPickerView("collections");
        setSelectedCollectionId(null);
        setSelectedQuestionId("");
        setSelectedRefIndices(new Set());
        setPickerSearch("");
        setPickerSpecialtyFilter("all");
        loadCollections();
    };

    const getFilteredQuestions = (): EligibleQuestion[] => {
        return pickerQuestions.filter((q) => {
            if (pickerSpecialtyFilter !== "all" && q.specialty !== pickerSpecialtyFilter) return false;
            if (pickerSearch.trim()) {
                const query = pickerSearch.toLowerCase();
                return q.question_text.toLowerCase().includes(query);
            }
            return true;
        });
    };

    const getSpecialtiesInCollection = (): string[] => {
        const set = new Set(pickerQuestions.map(q => q.specialty).filter(Boolean));
        return Array.from(set).sort();
    };

    const getSelectedCollectionTitle = (): string => {
        return pickerCollections.find(c => c.id === selectedCollectionId)?.title || "Questions";
    };

    const toggleRefIndex = (index: number) => {
        setSelectedRefIndices((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const getSelectedQuestion = (): EligibleQuestion | undefined => {
        return pickerQuestions.find((q) => q.id === selectedQuestionId);
    };

    const handleGenerate = async () => {
        if (!user?.id || !selectedQuestionId) return;

        setGenerating(true);
        try {
            const selectedQ = getSelectedQuestion();
            const hasRefs = (selectedQ?.question_references?.length || 0) > 0;
            const body: Record<string, unknown> = {
                user_id: user.id,
                question_id: selectedQuestionId,
            };
            if (hasRefs) {
                body.selected_reference_indices = Array.from(selectedRefIndices);
            }
            const res = await authenticatedFetch(`${apiUrl}/api/admin/instagram/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (res.ok) {
                const data = await res.json();
                setShowGenerateModal(false);
                setSelectedContent(data);
                setShowPreviewModal(true);
                loadContents();
            } else {
                const error = await res.json();
                alert(error.error || "Failed to generate content");
            }
        } catch (err) {
            console.error("Failed to generate:", err);
            alert("Failed to generate content");
        } finally {
            setGenerating(false);
        }
    };

    const handleRegenerate = async (contentId: string) => {
        if (!user?.id) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/regenerate`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                loadContents();
            } else {
                const error = await res.json();
                alert(error.error || "Failed to regenerate");
            }
        } catch (err) {
            console.error("Failed to regenerate:", err);
        }
    };

    const handleRegenerateCaption = async (contentId: string) => {
        if (!user?.id) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/regenerate-caption`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                const updated = await res.json();
                setSelectedContent(updated);
                loadContents();
            } else {
                const error = await res.json();
                alert(error.error || "Failed to regenerate caption");
            }
        } catch (err) {
            console.error("Failed to regenerate caption:", err);
        }
    };

    const handleSaveCaption = async () => {
        if (!user?.id || !selectedContent) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${selectedContent.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        caption: editedCaption,
                        hashtags: editedHashtags,
                    }),
                }
            );

            if (res.ok) {
                const updated = await res.json();
                setSelectedContent(updated);
                setEditingCaption(false);
                loadContents();
            }
        } catch (err) {
            console.error("Failed to save caption:", err);
        }
    };

    const handleMarkPosted = async (contentId: string, postUrl?: string) => {
        if (!user?.id) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/mark-posted`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        instagram_post_url: postUrl,
                    }),
                }
            );

            if (res.ok) {
                loadContents();
                setShowPreviewModal(false);
            }
        } catch (err) {
            console.error("Failed to mark as posted:", err);
        }
    };

    const handleDelete = async (contentId: string) => {
        if (!user?.id) return;
        if (!window.confirm("Are you sure you want to delete this content?")) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}`,
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                loadContents();
                setShowPreviewModal(false);
            }
        } catch (err) {
            console.error("Failed to delete:", err);
        }
    };

    const handleOpenScheduleModal = (contentId: string, initialDate?: string | null, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setSchedulingContentId(contentId);
        setSchedulingInitialDate(initialDate || null);
        setShowScheduleModal(true);
    };

    const handleSchedule = async (datetime: Date) => {
        if (!user?.id || !schedulingContentId) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${schedulingContentId}/schedule`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        user_id: user.id,
                        scheduled_for: datetime.toISOString(),
                    }),
                }
            );

            if (res.ok) {
                const updated = await res.json();
                // Update local state
                if (selectedContent?.id === schedulingContentId) {
                    setSelectedContent(updated);
                }
                loadContents();
                setShowScheduleModal(false);
                setSchedulingContentId(null);
                setSchedulingInitialDate(null);
            } else {
                const error = await res.json();
                alert(error.error || "Failed to schedule content");
            }
        } catch (err) {
            console.error("Failed to schedule:", err);
            alert("Failed to schedule content");
        }
    };

    const handleUnschedule = async (contentId: string) => {
        if (!user?.id) return;

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/schedule`,
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            if (res.ok) {
                const updated = await res.json();
                if (selectedContent?.id === contentId) {
                    setSelectedContent(updated);
                }
                loadContents();
            } else {
                const error = await res.json();
                alert(error.error || "Failed to unschedule content");
            }
        } catch (err) {
            console.error("Failed to unschedule:", err);
        }
    };

    const handlePublish = async (contentId: string) => {
        if (!user?.id) return;

        if (!window.confirm("Post this content to Instagram now?")) return;

        setPublishing(true);
        setPublishError(null);

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/publish`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            const data = await res.json();

            if (data.success) {
                // Update the selected content with the response
                if (data.content) {
                    setSelectedContent(data.content);
                }
                loadContents();
                alert("Successfully posted to Instagram!");
            } else {
                setPublishError(data.error || "Failed to publish");
                alert(`Failed to publish: ${data.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Failed to publish:", err);
            setPublishError("Network error");
            alert("Failed to publish: Network error");
        } finally {
            setPublishing(false);
        }
    };

    const handleRetry = async (contentId: string) => {
        if (!user?.id) return;

        setPublishing(true);
        setPublishError(null);

        try {
            const res = await authenticatedFetch(
                `${apiUrl}/api/admin/instagram/${contentId}/retry`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user.id }),
                }
            );

            const data = await res.json();

            if (data.success) {
                if (data.content) {
                    setSelectedContent(data.content);
                }
                loadContents();
                alert("Successfully posted to Instagram!");
            } else {
                setPublishError(data.error || "Failed to retry");
                alert(`Failed to retry: ${data.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Failed to retry:", err);
            setPublishError("Network error");
            alert("Failed to retry: Network error");
        } finally {
            setPublishing(false);
        }
    };

    const formatScheduledTime = (isoString: string | null): string => {
        if (!isoString) return "";
        const date = new Date(isoString);
        return date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const openPreview = (content: InstagramContent) => {
        setSelectedContent(content);
        setEditedCaption(content.caption || "");
        setEditedHashtags(content.hashtags || []);
        setEditingCaption(false);
        setShowPreviewModal(true);
    };

    const formatHashtags = (hashtags: string[] | null) => {
        if (!hashtags || hashtags.length === 0) return "";
        return hashtags.map((h) => `#${h}`).join(" ");
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        // Could add a toast notification here
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>Loading...</div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1>Instagram Content · {activeBank.toUpperCase()}</h1>
                    <select value={activeBank} onChange={(event) => {
                        setActiveBank(event.target.value as QBankCode);
                        setPage(1);
                        setPickerCollections([]);
                        setPickerQuestions([]);
                    }} aria-label="QBank">
                        <option value="sdle">SDLE</option>
                        <option value="smle">SMLE</option>
                    </select>
                </div>
                <div className={styles.headerActions}>
                    <button
                        className={styles.secondaryButton}
                        onClick={() => navigate(`/admin/instagram/calendar?bank=${activeBank}`)}
                    >
                        Calendar
                    </button>
                    <button
                        className={styles.primaryButton}
                        onClick={handleOpenGenerateModal}
                    >
                        + New Content
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                {(["all", "ready", "scheduled", "posted"] as const).map((tab) => (
                    <button
                        key={tab}
                        className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
                        onClick={() => {
                            setActiveTab(tab);
                            setPage(1);
                        }}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Content List */}
            {contents.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No Instagram content yet.</p>
                    <button
                        className={styles.primaryButton}
                        onClick={handleOpenGenerateModal}
                    >
                        Generate Your First Content
                    </button>
                </div>
            ) : (
                <div className={styles.grid}>
                    {contents.map((content) => (
                        <div
                            key={content.id}
                            className={styles.card}
                            onClick={() => openPreview(content)}
                        >
                            {/* Thumbnail */}
                            <div className={styles.thumbnail}>
                                {content.images?.slide1_question ? (
                                    <img
                                        src={content.images.slide1_question}
                                        alt="Slide 1"
                                    />
                                ) : (
                                    <div className={styles.placeholderThumb}>
                                        {content.status === "generating" ? "..." : "?"}
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <div className={styles.cardInfo}>
                                <div className={styles.cardBadges}>
                                    <span
                                        className={styles.statusBadge}
                                        style={{
                                            backgroundColor: STATUS_LABELS[content.status]?.color || "#ccc",
                                        }}
                                    >
                                        {STATUS_LABELS[content.status]?.label || content.status}
                                    </span>
                                    {content.scheduling_status === "scheduled" && content.scheduled_for && (
                                        <span
                                            className={styles.scheduleBadge}
                                            title={formatScheduledTime(content.scheduled_for)}
                                        >
                                            {formatScheduledTime(content.scheduled_for)}
                                        </span>
                                    )}
                                    {content.scheduling_status === "failed" && (
                                        <span
                                            className={styles.failedBadge}
                                            title={content.last_publish_error || "Publishing failed"}
                                        >
                                            Failed
                                        </span>
                                    )}
                                    {content.scheduling_status === "posting" && (
                                        <span className={styles.postingBadge}>
                                            Posting...
                                        </span>
                                    )}
                                </div>
                                <p className={styles.questionPreview}>
                                    {content.qbank_questions?.question_text?.slice(0, 60) || "Question"}...
                                </p>
                                <div className={styles.cardFooter}>
                                    <span className={styles.specialty}>
                                        {content.qbank_questions?.specialty || "Unknown"}
                                    </span>
                                    {content.status === "ready" && content.scheduling_status === "failed" && (
                                        <button
                                            className={styles.retryButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRetry(content.id);
                                            }}
                                            title="Retry posting"
                                        >
                                            Retry
                                        </button>
                                    )}
                                    {content.status === "ready" && content.scheduling_status === "unscheduled" && (
                                        <button
                                            className={styles.scheduleButton}
                                            onClick={(e) => handleOpenScheduleModal(content.id, null, e)}
                                            title="Schedule post"
                                        >
                                            Schedule
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                    >
                        Previous
                    </button>
                    <span>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Generate Modal */}
            {showGenerateModal && (
                <div className={styles.modalOverlay} onClick={() => setShowGenerateModal(false)}>
                    <div
                        className={`${styles.modal} ${styles.questionPickerModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            {pickerView === "questions" && (
                                <button
                                    className={styles.pickerBackButton}
                                    onClick={handlePickerBack}
                                    title="Back to collections"
                                >
                                    &larr;
                                </button>
                            )}
                            <h2>
                                {pickerView === "collections"
                                    ? "Select a Collection"
                                    : getSelectedCollectionTitle()}
                            </h2>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowGenerateModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className={styles.pickerBody}>
                            {pickerView === "collections" ? (
                                /* Collection list view */
                                loadingCollections ? (
                                    <div className={styles.pickerLoading}>
                                        <div className={styles.pickerSpinner} />
                                    </div>
                                ) : pickerCollections.length === 0 ? (
                                    <p className={styles.noQuestions}>
                                        No eligible questions found. Questions must have completed
                                        explanations and not already have Instagram content.
                                    </p>
                                ) : (
                                    <>
                                        <p className={styles.pickerSubtitle}>
                                            {pickerTotalEligible} eligible question{pickerTotalEligible !== 1 ? "s" : ""} across{" "}
                                            {pickerCollections.length} collection{pickerCollections.length !== 1 ? "s" : ""}
                                        </p>
                                        <div className={styles.collectionList}>
                                            {pickerCollections.map((col) => (
                                                <div
                                                    key={col.id}
                                                    className={styles.collectionCard}
                                                    onClick={() => handleSelectCollection(col.id)}
                                                >
                                                    <div className={styles.collectionCardInfo}>
                                                        <span className={styles.collectionTitle}>{col.title}</span>
                                                        <span className={styles.collectionMeta}>
                                                            {col.year} &middot; Month {col.month}
                                                        </span>
                                                    </div>
                                                    <span className={styles.collectionCount}>
                                                        {col.eligible_count} of {col.questions_count}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )
                            ) : (
                                /* Questions list view */
                                loadingQuestions ? (
                                    <div className={styles.pickerLoading}>
                                        <div className={styles.pickerSpinner} />
                                    </div>
                                ) : pickerQuestions.length === 0 ? (
                                    <p className={styles.pickerEmpty}>No eligible questions in this collection.</p>
                                ) : (
                                    <>
                                        <div className={styles.pickerControls}>
                                            <div className={styles.pickerSearchGroup}>
                                                <input
                                                    type="text"
                                                    className={styles.pickerSearchInput}
                                                    placeholder="Search questions..."
                                                    value={pickerSearch}
                                                    onChange={(e) => setPickerSearch(e.target.value)}
                                                />
                                                {pickerSearch && (
                                                    <button
                                                        className={styles.pickerClearSearch}
                                                        onClick={() => setPickerSearch("")}
                                                    >
                                                        &times;
                                                    </button>
                                                )}
                                            </div>
                                            {getSpecialtiesInCollection().length > 1 && (
                                                <select
                                                    className={styles.pickerFilterSelect}
                                                    value={pickerSpecialtyFilter}
                                                    onChange={(e) => setPickerSpecialtyFilter(e.target.value)}
                                                >
                                                    <option value="all">All specialties</option>
                                                    {getSpecialtiesInCollection().map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <div className={styles.pickerQuestions}>
                                            {getFilteredQuestions().length === 0 ? (
                                                <p className={styles.pickerEmpty}>No questions match your search.</p>
                                            ) : (
                                                getFilteredQuestions().map((q) => (
                                                    <div
                                                        key={q.id}
                                                        className={`${styles.pickerQuestionCard} ${
                                                            selectedQuestionId === q.id ? styles.pickerQuestionSelected : ""
                                                        }`}
                                                        onClick={() => {
                                                            if (selectedQuestionId === q.id) {
                                                                setSelectedQuestionId("");
                                                                setSelectedRefIndices(new Set());
                                                            } else {
                                                                setSelectedQuestionId(q.id);
                                                                const refs = q.question_references || [];
                                                                setSelectedRefIndices(new Set(refs.map((_, i) => i)));
                                                            }
                                                        }}
                                                    >
                                                        <div className={styles.pickerQuestionHeader}>
                                                            <span className={styles.pickerSpecialtyTag}>{q.specialty}</span>
                                                            {q.difficulty && (
                                                                <span className={styles.pickerDifficultyTag}>{q.difficulty}</span>
                                                            )}
                                                            {selectedQuestionId === q.id && (
                                                                <span className={styles.pickerSelectedIndicator}>Selected</span>
                                                            )}
                                                        </div>
                                                        <p
                                                            className={
                                                                selectedQuestionId === q.id
                                                                    ? styles.pickerQuestionText
                                                                    : `${styles.pickerQuestionText} ${styles.pickerQuestionTextTruncated}`
                                                            }
                                                        >
                                                            {q.question_text}
                                                        </p>
                                                        {selectedQuestionId === q.id && q.options && (
                                                            <div className={styles.pickerOptions}>
                                                                {q.options.map((opt) => (
                                                                    <div
                                                                        key={opt.label}
                                                                        className={`${styles.pickerOption} ${
                                                                            opt.label === q.correct_answer
                                                                                ? styles.pickerOptionCorrect
                                                                                : ""
                                                                        }`}
                                                                    >
                                                                        <span className={styles.pickerOptionLabel}>{opt.label}.</span>
                                                                        {opt.text}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {selectedQuestionId === q.id && q.question_references && q.question_references.length > 0 && (
                                                            <div className={styles.referencesSection} onClick={(e) => e.stopPropagation()}>
                                                                <div className={styles.referencesHeader}>
                                                                    <span className={styles.referencesTitle}>
                                                                        References ({selectedRefIndices.size} of {q.question_references.length} selected)
                                                                    </span>
                                                                    <button
                                                                        className={styles.referencesToggleAll}
                                                                        onClick={() => {
                                                                            if (selectedRefIndices.size === q.question_references!.length) {
                                                                                setSelectedRefIndices(new Set());
                                                                            } else {
                                                                                setSelectedRefIndices(new Set(q.question_references!.map((_, i) => i)));
                                                                            }
                                                                        }}
                                                                    >
                                                                        {selectedRefIndices.size === q.question_references.length ? "Deselect All" : "Select All"}
                                                                    </button>
                                                                </div>
                                                                {q.question_references.map((ref, idx) => (
                                                                    <label key={idx} className={styles.referenceRow}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedRefIndices.has(idx)}
                                                                            onChange={() => toggleRefIndex(idx)}
                                                                            className={styles.referenceCheckbox}
                                                                        />
                                                                        <div className={styles.referenceInfo}>
                                                                            <span className={styles.referenceSource}>{ref.source_name}</span>
                                                                            <span className={styles.referenceExcerpt}>
                                                                                {ref.text_excerpt.length > 150
                                                                                    ? ref.text_excerpt.slice(0, 150) + "..."
                                                                                    : ref.text_excerpt}
                                                                            </span>
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )
                            )}
                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                className={styles.secondaryButton}
                                onClick={() => setShowGenerateModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.primaryButton}
                                onClick={handleGenerate}
                                disabled={
                                    !selectedQuestionId ||
                                    generating ||
                                    ((getSelectedQuestion()?.question_references?.length || 0) > 0 && selectedRefIndices.size === 0)
                                }
                            >
                                {generating ? "Generating..." : "Generate"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && selectedContent && (
                <div className={styles.modalOverlay} onClick={() => setShowPreviewModal(false)}>
                    <div
                        className={`${styles.modal} ${styles.previewModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h2>Content Preview</h2>
                            <button
                                className={styles.closeButton}
                                onClick={() => setShowPreviewModal(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <div className={styles.previewContent}>
                            {/* Images */}
                            <div className={styles.slidePreview}>
                                {selectedContent.images?.slide1_question && (
                                    <div className={styles.slide}>
                                        <span className={styles.slideLabel}>Slide 1: Question</span>
                                        <img src={selectedContent.images.slide1_question} alt="Question" />
                                        <a
                                            href={selectedContent.images.slide1_question}
                                            download="slide1_question.png"
                                            className={styles.downloadLink}
                                        >
                                            Download
                                        </a>
                                    </div>
                                )}
                                {selectedContent.images?.slide2_answer && (
                                    <div className={styles.slide}>
                                        <span className={styles.slideLabel}>Slide 2: Answer</span>
                                        <img src={selectedContent.images.slide2_answer} alt="Answer" />
                                        <a
                                            href={selectedContent.images.slide2_answer}
                                            download="slide2_answer.png"
                                            className={styles.downloadLink}
                                        >
                                            Download
                                        </a>
                                    </div>
                                )}
                                {selectedContent.images?.slide3_sources && (
                                    <div className={styles.slide}>
                                        <span className={styles.slideLabel}>Slide 3: Sources</span>
                                        <img src={selectedContent.images.slide3_sources} alt="Sources" />
                                        <a
                                            href={selectedContent.images.slide3_sources}
                                            download="slide3_sources.png"
                                            className={styles.downloadLink}
                                        >
                                            Download
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Caption */}
                            <div className={styles.captionSection}>
                                <div className={styles.captionHeader}>
                                    <h3>Caption</h3>
                                    {!editingCaption && (
                                        <div className={styles.captionActions}>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => setEditingCaption(true)}
                                                title="Edit"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => handleRegenerateCaption(selectedContent.id)}
                                                title="Regenerate Caption"
                                            >
                                                Regenerate
                                            </button>
                                            <button
                                                className={styles.iconButton}
                                                onClick={() =>
                                                    copyToClipboard(
                                                        `${selectedContent.caption || ""}\n\n${formatHashtags(selectedContent.hashtags)}`
                                                    )
                                                }
                                                title="Copy All"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {editingCaption ? (
                                    <div className={styles.editCaption}>
                                        <textarea
                                            value={editedCaption}
                                            onChange={(e) => setEditedCaption(e.target.value)}
                                            rows={6}
                                        />
                                        <input
                                            type="text"
                                            value={editedHashtags.join(", ")}
                                            onChange={(e) =>
                                                setEditedHashtags(
                                                    e.target.value.split(",").map((h) => h.trim().replace("#", ""))
                                                )
                                            }
                                            placeholder="Hashtags (comma separated)"
                                        />
                                        <div className={styles.editActions}>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => setEditingCaption(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={handleSaveCaption}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.captionDisplay}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {selectedContent.caption || ""}
                                        </ReactMarkdown>
                                        <p className={styles.hashtags}>
                                            {formatHashtags(selectedContent.hashtags)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Error display */}
                        {selectedContent.scheduling_status === "failed" && selectedContent.last_publish_error && (
                            <div className={styles.errorBanner}>
                                <strong>Publishing failed:</strong> {selectedContent.last_publish_error}
                            </div>
                        )}

                        {/* Posted link */}
                        {selectedContent.status === "posted" && selectedContent.instagram_post_url && (
                            <div className={styles.successBanner}>
                                <strong>Posted!</strong>{" "}
                                <a
                                    href={selectedContent.instagram_post_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View on Instagram
                                </a>
                            </div>
                        )}

                        <div className={styles.modalFooter}>
                            <button
                                className={styles.dangerButton}
                                onClick={() => handleDelete(selectedContent.id)}
                            >
                                Delete
                            </button>
                            <button
                                className={styles.secondaryButton}
                                onClick={() => handleRegenerate(selectedContent.id)}
                            >
                                Regenerate All
                            </button>
                            {selectedContent.status === "ready" && (
                                <>
                                    {selectedContent.scheduling_status === "failed" ? (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleOpenScheduleModal(selectedContent.id, null)}
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handleRetry(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Retry Post"}
                                            </button>
                                        </>
                                    ) : selectedContent.scheduling_status === "scheduled" ? (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleUnschedule(selectedContent.id)}
                                            >
                                                Unschedule
                                            </button>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleMarkPosted(selectedContent.id)}
                                            >
                                                Mark as Posted
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handlePublish(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Post Now"}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleOpenScheduleModal(selectedContent.id, null)}
                                            >
                                                Schedule
                                            </button>
                                            <button
                                                className={styles.secondaryButton}
                                                onClick={() => handleMarkPosted(selectedContent.id)}
                                            >
                                                Mark as Posted
                                            </button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={() => handlePublish(selectedContent.id)}
                                                disabled={publishing}
                                            >
                                                {publishing ? "Posting..." : "Post Now"}
                                            </button>
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Scheduling Info in Modal */}
                        {selectedContent.scheduling_status === "scheduled" && selectedContent.scheduled_for && (
                            <div className={styles.schedulingInfo}>
                                Scheduled for: {formatScheduledTime(selectedContent.scheduled_for)}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && schedulingContentId && (
                <ScheduleModal
                    onSchedule={handleSchedule}
                    onClose={() => {
                        setShowScheduleModal(false);
                        setSchedulingContentId(null);
                        setSchedulingInitialDate(null);
                    }}
                    initialDate={schedulingInitialDate}
                />
            )}
        </div>
    );
};

export default AdminInstagram;
